import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { GitCompareArrows, Loader2, FileClock, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/apiClient";

export default function CompareStates() {
  const [tax, setTax] = useState(null);
  const [picked, setPicked] = useState([]);
  const [filters, setFilters] = useState({ setting: "", org_type: "", role: "", payer: "" });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/compliance/taxonomy").then((r) => setTax(r.data)).catch(() => {}); }, []);

  const toggle = (slug) => setPicked((p) => p.includes(slug) ? p.filter((s) => s !== slug) : (p.length >= 4 ? p : [...p, slug]));

  const run = async () => {
    if (picked.length < 2) return;
    setLoading(true); setResult(null);
    try {
      const body = { states: picked };
      Object.entries(filters).forEach(([k, v]) => { if (v) body[k] = v; });
      const { data } = await api.post("/compliance/compare", body);
      setResult(data);
    } catch { /* noop */ } finally { setLoading(false); }
  };

  const cellFor = (col, catId) => (col.categories.find((c) => c.id === catId)) || { status: "pending", requirements: [] };

  return (
    <div>
      <section className="bg-blue-950 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <Link to="/compliance" className="inline-flex items-center gap-1.5 text-sm text-teal-300 mb-5 hover:gap-2.5 transition-all"><ArrowLeft className="w-4 h-4" /> Navigator</Link>
          <p className="text-xs uppercase tracking-[0.16em] text-teal-400 font-semibold mb-2 flex items-center gap-2"><GitCompareArrows className="w-4 h-4" /> Multi-state comparison</p>
          <h1 className="font-display text-4xl lg:text-5xl font-bold">Compare States</h1>
          <p className="mt-4 text-blue-100/80 max-w-2xl text-lg">Pick 2–4 states to compare licensing, training, credentialing, screening and program requirements side by side.</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-2xl border border-blue-900/5 shadow-sm p-6">
          <h2 className="font-display text-lg font-bold text-blue-950 mb-3">Select states <span className="text-slate-400 font-normal text-sm">({picked.length}/4)</span></h2>
          <div className="flex flex-wrap gap-2 max-h-52 overflow-y-auto" data-testid="compare-state-chips">
            {tax?.states.map((s) => (
              <button key={s.slug} data-testid={`compare-chip-${s.slug}`} onClick={() => toggle(s.slug)}
                className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${picked.includes(s.slug) ? "bg-teal-600 text-white border-teal-600" : "bg-white text-blue-900 border-blue-900/10 hover:border-teal-500"}`}>
                {s.name}
              </button>
            ))}
          </div>
          <div className="grid sm:grid-cols-4 gap-3 mt-5">
            {[["setting", "care_settings", "Care setting"], ["org_type", "org_types", "Org type"], ["role", "worker_roles", "Worker role"], ["payer", "payer_types", "Payer"]].map(([key, tk, label]) => (
              <Select key={key} value={filters[key]} onValueChange={(v) => setFilters({ ...filters, [key]: v })}>
                <SelectTrigger data-testid={`compare-${key}`}><SelectValue placeholder={label} /></SelectTrigger>
                <SelectContent className="max-h-72">{(tax?.[tk] || []).map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
              </Select>
            ))}
          </div>
          <Button onClick={run} disabled={picked.length < 2 || loading} data-testid="compare-run" className="mt-5 h-11 bg-teal-600 hover:bg-teal-700 text-white px-8">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Compare"}
          </Button>
        </div>

        {result && (
          <div className="mt-8 overflow-x-auto" data-testid="compare-result">
            <table className="w-full border-collapse min-w-[640px]">
              <thead>
                <tr>
                  <th className="text-left text-sm font-semibold text-slate-500 p-3 w-56">Requirement category</th>
                  {result.columns.map((col) => (
                    <th key={col.state.slug} className="text-left p-3">
                      <Link to={`/compliance/${col.state.slug}`} className="font-display font-bold text-blue-950 hover:text-teal-700">{col.state.name}</Link>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {result.categories.map((cat) => (
                  <tr key={cat.id} className="border-t border-blue-900/5">
                    <td className="p-3 text-sm font-medium text-blue-950 align-top">{cat.name}</td>
                    {result.columns.map((col) => {
                      const cell = cellFor(col, cat.id);
                      return (
                        <td key={col.state.slug} className="p-3 align-top">
                          {cell.status === "verified"
                            ? <span className="text-[11px] font-semibold uppercase tracking-wide bg-teal-100 text-teal-800 px-2.5 py-1 rounded-full">{cell.requirements.length} verified</span>
                            : <span data-testid="verification-pending" className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide bg-amber-100 text-amber-800 px-2.5 py-1 rounded-full"><FileClock className="w-3 h-3" /> Pending</span>}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
